import { useEffect, useState } from "react";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import useDebouncedValue from "hooks/useDebouncedValue";

export type Option = {
  label: string;
  value: string | number;
};

type Props = {
  label: string;
  value: Option[];
  onChange: (value: Option[]) => void;
  fetchOptions: (search: string) => Promise<Option[]>;
  disabled?: boolean;
};

const DEBOUNCE_MS = 400;

function AsyncMultiSelect({ label, value, onChange, fetchOptions, disabled }: Props) {
  const [inputValue, setInputValue] = useState("");
  const debouncedInput = useDebouncedValue(inputValue, DEBOUNCE_MS);
  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchOptions(debouncedInput)
      .then((results) => {
        if (!cancelled) setOptions(results);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedInput]);

  return (
    <Autocomplete
      multiple
      disabled={disabled}
      options={options}
      value={value}
      loading={loading}
      isOptionEqualToValue={(option, selected) => option.value === selected.value}
      getOptionLabel={(option) => option.label}
      filterOptions={(opts) => opts}
      onChange={(_e, newValue) => onChange(newValue)}
      onInputChange={(_e, newInput) => setInputValue(newInput)}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

export default AsyncMultiSelect;
